import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { supabase } from 'services/SupaService'
import { onSignInSuccess } from 'store/auth/sessionSlice'
import { setUser } from 'store/auth/userSlice'

// Picks up a Supabase session and pushes it into the app's Redux auth
// state. Used by scripts/impersonate.js for local debugging.
//
// Activation: once the URL contains ?impersonate=1 we set a sessionStorage
// flag. The flag is sticky for the tab's lifetime, which matters because
// the route guard often navigates away to /sign-in before the bridge has
// finished, stripping the query param. With the flag in sessionStorage we
// survive that bounce — the SDK eventually fires SIGNED_IN, we dispatch,
// and the guard re-evaluates as authenticated.
//
// We listen to onAuthStateChange (instead of just calling getSession once)
// because the Supabase SDK parses the magic-link hash asynchronously after
// page load, and we want to react whenever it does.
const FLAG_KEY = 'impersonate_active'

function isFlagInUrl() {
    const params = new URLSearchParams(window.location.search)
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''))
    return params.get('impersonate') === '1' || hash.get('impersonate') === '1'
}

function SupabaseSessionBridge() {
    const dispatch = useDispatch()

    useEffect(() => {
        if (isFlagInUrl()) {
            try {
                sessionStorage.setItem(FLAG_KEY, '1')
            } catch (_) {
            // sessionStorage may be unavailable (private mode) — flag stays off
        }
        }
        let active = false
        try {
            active = sessionStorage.getItem(FLAG_KEY) === '1'
        } catch (_) {
            // sessionStorage may be unavailable (private mode) — flag stays off
        }
        if (!active) return

        const apply = (session) => {
            if (!session) return
            dispatch(onSignInSuccess(session.access_token))
            if (session.user) dispatch(setUser(session.user))
        }

        // Pick up an already-established session immediately.
        supabase.auth.getSession().then(({ data }) => apply(data?.session))

        // Catch the SIGNED_IN event the SDK fires after parsing the hash.
        const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
            apply(session)
        })

        return () => {
            sub?.subscription?.unsubscribe?.()
        }
    }, [dispatch])

    return null
}

export default SupabaseSessionBridge
